import axios from 'axios';
import type { ApiEnvelope } from '@/types';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/v1';

const apiClient = axios.create({
  baseURL: API_BASE,
  // 240s — AI 识别/报告/内容生成较慢(后端 L3 超时 90s + backup 回退)。
  // 必须小于 nginx /v1/ 的 proxy_read_timeout 300s, 让客户端先超时(可提示重试)
  // 而不是拿到网关 504(此时后端仍在跑并照常结算积分, 用户白扣费)。
  timeout: 240000,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // FormData 需要浏览器自动设置 multipart/form-data + boundary
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const { data } = await axios.post<ApiEnvelope<{ access_token: string }>>(`${API_BASE}/auth/refresh`, {
            refresh_token: refreshToken,
          });
          localStorage.setItem('access_token', data.data.access_token);
          originalRequest.headers.Authorization = `Bearer ${data.data.access_token}`;
          return apiClient(originalRequest);
        } catch {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  },
);

export async function apiGet<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const { data } = await apiClient.get<ApiEnvelope<T>>(url, { params });
  return data.data;
}

export async function apiPost<T>(url: string, body?: unknown, timeout?: number): Promise<T> {
  const { data } = await apiClient.post<ApiEnvelope<T>>(url, body, timeout ? { timeout } : undefined);
  return data.data;
}

export async function apiPut<T>(url: string, body?: unknown): Promise<T> {
  const { data } = await apiClient.put<ApiEnvelope<T>>(url, body);
  return data.data;
}

export async function apiPatch<T>(url: string, body?: unknown): Promise<T> {
  const { data } = await apiClient.patch<ApiEnvelope<T>>(url, body);
  return data.data;
}

export async function apiDelete<T>(url: string, body?: unknown): Promise<T> {
  const { data } = await apiClient.delete<ApiEnvelope<T>>(url, { data: body });
  return data.data;
}

export default apiClient;
